/**
 * Страница конкурса: мои билеты с номерами, призы, условия, итоги.
 *
 * Описание и названия — только текстом (React экранирует), без HTML:
 * их вводит администратор, но в приложении у всех они не исполняются.
 */
import { useNavigate, useParams } from 'react-router';

import { drawText, shortDate, useRaffle } from '@/features/raffle/raffles';
import { useBackButton } from '@/shared/lib/useBackButton';
import { ticketNo } from '@/shared/lib/tickets';
import { Screen } from '@/shared/ui/Screen';
import { Button } from '@/shared/ui/kit/Button';

import styles from './Raffle.module.css';

const number = new Intl.NumberFormat('ru-RU');
const rub = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });

/** Места призов по порядку: «1–10 место», «11 место». */
function placesOf(prizes: { quantity: number }[]): string[] {
  let next = 1;
  return prizes.map((p) => {
    const first = next;
    next += p.quantity;
    const last = next - 1;
    return first === last ? `${first} место` : `${first}–${last} место`;
  });
}

export function RafflePage() {
  const navigate = useNavigate();
  const { id } = useParams();
  useBackButton(() => void navigate('/raffles'));
  const { data, isLoading, error, refetch } = useRaffle(id);
  const places = data ? placesOf(data.prizes) : [];
  const myWins = data?.winners.filter((w) => w.is_me) ?? [];

  return (
    <Screen isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      {data ? (
        <>
          {data.image_url ? (
            <img
              className={styles.image}
              style={{ borderRadius: 'var(--r-lg)' }}
              src={data.image_url}
              alt=""
              referrerPolicy="no-referrer"
            />
          ) : null}
          <h1 className={styles.head}>{data.title}</h1>
          <span className={styles.chip}>{drawText(data)}</span>

          {myWins.length > 0 ? (
            <div className={styles.win}>
              <p className={styles.winTitle}>Ваш билет выиграл!</p>
              {myWins.map((w) => (
                <p key={w.place} className={styles.text}>
                  Билет <span className={styles.mono}>{w.ticket_no !== null ? ticketNo(w.ticket_no) : '—'}</span> —{' '}
                  {w.place} место, {w.prize ?? 'приз'}
                </p>
              ))}
              <p className={styles.text}>Мы напишем вам в Telegram, чтобы договориться о получении.</p>
            </div>
          ) : null}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>
              Мои билеты{data.my_tickets > 0 ? ` · ${number.format(data.my_tickets)}` : ''}
            </h2>
            {data.my_ranges.length === 0 ? (
              <p className={styles.text}>Пока нет. Билеты придут с ближайшей оплатой подписки.</p>
            ) : (
              <div className={styles.tickets}>
                {data.my_ranges.flatMap((r) => {
                  // Каждый билет — своей карточкой; у старых без номеров — одна на покупку.
                  const label = `${r.voided ? 'аннулирован' : r.months ? `${r.months} мес.` : 'оплата'} · ${shortDate(r.issued_at)}`;
                  const first = r.first_no;
                  const numbers = first !== null ? Array.from({ length: r.count }, (_, i) => first + i) : [null];
                  return numbers.map((n) => (
                    <div key={`${r.first_no}-${r.issued_at}-${n}`} className={`${styles.ticket} ${r.voided ? styles.voided : ''}`}>
                      <span className={styles.ticketNo}>{n !== null ? ticketNo(n) : `${r.count} шт.`}</span>
                      <span className={styles.statLabel}>{label}</span>
                    </div>
                  ));
                })}
              </div>
            )}
          </section>

          {data.prizes.length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Призы</h2>
              {data.prizes.map((p, index) => (
                <div key={`${p.title}-${index}`} className={styles.row}>
                  <span>{places[index]}</span>
                  <span className={styles.muted}>
                    {p.title}
                    {p.quantity > 1 ? ` × ${p.quantity}` : ''}
                  </span>
                </div>
              ))}
              <p className={styles.note}>
                Если отправить приз победителю невозможно — выплатим его стоимость деньгами
                {data.prizes[0]?.value_minor ? ` (${rub.format(data.prizes[0].value_minor / 100)} ₽)` : ''}.
              </p>
            </section>
          ) : null}

          {data.status === 'drawn' && data.winners.length > 0 ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Победители</h2>
              {data.winners.map((w) => (
                <div key={w.place} className={styles.row}>
                  <span className={w.is_me ? styles.mine : styles.muted}>
                    {w.place} · {w.name}
                  </span>
                  <span className={`${styles.mono} ${w.is_me ? styles.mine : ''}`}>
                    {w.ticket_no !== null ? ticketNo(w.ticket_no) : '—'}
                  </span>
                </div>
              ))}
              <p className={styles.text}>
                {data.manual_draw
                  ? 'Номера победителей выбраны рандомайзером.'
                  : 'Номера победителей выбраны случайно системой.'}
              </p>
            </section>
          ) : null}

          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Условия</h2>
            <p className={styles.text}>
              1 месяц подписки — 1 билет, 1 год — 12 билетов, продления тоже считаются. Конкурс идёт с{' '}
              {shortDate(data.starts_at)}
              {data.ends_at ? ` по ${shortDate(data.ends_at)}` : ''}.
            </p>
            {data.description ? <p className={styles.text}>{data.description}</p> : null}
          </section>

          {data.status === 'active' ? (
            <Button block onClick={() => void navigate('/plans')}>
              Получить ещё билеты
            </Button>
          ) : null}
        </>
      ) : null}
    </Screen>
  );
}
